import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const SYNC_SOURCE = 'vestfirma-cart-wholesale';

function readConfig() {
  const node = document.getElementById('vestfirma-cart-wholesale-config');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}

function formatMoney(cents, format) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents, format);
  }
  const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
  return `R$ ${value}`;
}

function applyI18nTemplate(template, vars) {
  let output = template;
  Object.entries(vars).forEach(([key, value]) => {
    output = output.replaceAll(`{{ ${key} }}`, String(value));
    output = output.replaceAll(`__${key.toUpperCase()}__`, String(value));
  });
  return output;
}

function isWholesaleItem(item, config) {
  const props = item.properties || {};
  if (props._modo_venda === 'standard') return false;
  return Boolean(props._grupo_atacado || props[config.propGroup]);
}

const STALE_WHOLESALE_KEYS = [
  '_atacado_unit_cents',
  '_atacado_tier1_cents',
  '_atacado_tier2_cents',
  '_atacado_tier2_min',
  '_preco_atacado',
];

function hasStaleWholesaleProps(item, config) {
  if (isWholesaleItem(item, config)) return false;
  const props = item.properties || {};
  if (props[config.propUnitPrice] || props[config.propOrderTotal]) return true;
  return STALE_WHOLESALE_KEYS.some((key) => props[key] != null && props[key] !== '');
}

function stripWholesaleProperties(item, config) {
  const props = { ...(item.properties || {}) };
  STALE_WHOLESALE_KEYS.forEach((key) => {
    delete props[key];
  });
  delete props[config.propUnitPrice];
  delete props[config.propOrderTotal];
  return props;
}

function resolveTierConfig(item, config) {
  const props = item.properties || {};
  const tier1 = Number(props._atacado_tier1_cents) || Number(item.price) || 0;
  let tier2 = Number(props._atacado_tier2_cents) || 0;
  const tier2Min = Number(props._atacado_tier2_min) || Number(config.tier2Min) || 11;
  const storedUnit = Number(props._atacado_unit_cents) || 0;
  const discountCents = Math.round(Number(config.tier2DiscountReais || 2) * 100);

  if (!tier2 && storedUnit > 0 && tier1 > 0 && storedUnit < tier1) {
    tier2 = storedUnit;
  }

  if (!tier2 && tier1 > 0 && discountCents > 0) {
    tier2 = Math.max(0, tier1 - discountCents);
  }

  return { tier1, tier2, tier2Min };
}

function getUnitPrice(totalQty, tierConfig) {
  const { tier1, tier2, tier2Min } = tierConfig;
  if (totalQty >= tier2Min && tier2 > 0) return tier2;
  return tier1;
}

function buildGroups(items, config) {
  /** @type {Map<string, Array<any>>} */
  const groups = new Map();

  items.forEach((item) => {
    if (!isWholesaleItem(item, config)) return;
    const key = `product-${item.product_id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  return groups;
}

function buildPricingProperties(item, totalQty, unitPrice, tierConfig, config) {
  const unitMoney = formatMoney(unitPrice, config.moneyFormat);
  const merged = { ...(item.properties || {}) };

  merged[config.propUnitPrice] = unitMoney;
  merged[config.propOrderTotal] = String(totalQty);
  merged._atacado_unit_cents = String(unitPrice);
  merged._atacado_tier1_cents = String(tierConfig.tier1);
  merged._atacado_tier2_cents = String(tierConfig.tier2);
  merged._atacado_tier2_min = String(tierConfig.tier2Min);
  merged._preco_atacado = applyI18nTemplate(config.cartTierNote, {
    price: unitMoney,
    qty: totalQty,
  });

  return merged;
}

function pricingChanged(item, nextProperties, config) {
  const current = item.properties || {};
  return (
    current[config.propUnitPrice] !== nextProperties[config.propUnitPrice] ||
    current[config.propOrderTotal] !== nextProperties[config.propOrderTotal] ||
    current._atacado_unit_cents !== nextProperties._atacado_unit_cents
  );
}

function getSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids);
}

async function syncWholesalePricing(source) {
  if (source === SYNC_SOURCE) return;

  const config = readConfig();
  if (!config) return;

  const cart = await fetch('/cart.js').then((response) => response.json());
  if (!cart.items?.length) return;

  /** @type {Array<{ key: string, quantity: number, properties: Record<string, string> }>} */
  const updates = [];

  cart.items.forEach((line) => {
    if (!hasStaleWholesaleProps(line, config)) return;
    updates.push({
      key: line.key,
      quantity: line.quantity,
      properties: stripWholesaleProperties(line, config),
    });
  });

  const groups = buildGroups(cart.items, config);

  groups.forEach((lines) => {
    const totalQty = lines.reduce((sum, line) => sum + line.quantity, 0);
    const tierConfig = resolveTierConfig(lines[0], config);
    const unitPrice = getUnitPrice(totalQty, tierConfig);

    lines.forEach((line) => {
      const nextProperties = buildPricingProperties(line, totalQty, unitPrice, tierConfig, config);
      if (pricingChanged(line, nextProperties, config)) {
        updates.push({
          key: line.key,
          quantity: line.quantity,
          properties: nextProperties,
        });
      }
    });
  });

  if (!updates.length) return;

  const sectionIds = getSectionIds();
  let lastResponse = null;

  for (let index = 0; index < updates.length; index += 1) {
    const update = updates[index];
    const isLast = index === updates.length - 1;
    const body = {
      id: update.key,
      quantity: update.quantity,
      properties: update.properties,
    };

    if (isLast && sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) return;
    lastResponse = await response.json();
  }

  if (!lastResponse?.sections || !sectionIds.length) return;

  sectionIds.forEach((sectionId) => {
    const html = lastResponse.sections[sectionId];
    if (html) morphSection(sectionId, html);
  });

  const hiddenCount = document.querySelector('[ref="cartItemCount"]')?.textContent;
  const itemCount = hiddenCount ? parseInt(hiddenCount, 10) : cart.item_count;

  document.dispatchEvent(
    new CartUpdateEvent(lastResponse, sectionIds[0] || '', {
      itemCount,
      source: SYNC_SOURCE,
      sections: lastResponse.sections,
    })
  );
}

let syncTimer = 0;

function scheduleSync(source) {
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    syncWholesalePricing(source).catch((error) => {
      console.error('[vestfirma-cart-wholesale]', error);
    });
  }, 120);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = event.detail?.source || '';
  scheduleSync(source);
});

document.addEventListener('DOMContentLoaded', () => {
  scheduleSync('init');
});
